import React, { useState, useEffect } from "react";

const PokerCardGame = () => {
  // 定义卡牌
  const initialCards = [
    { id: 1, suit: "hearts", value: "A", symbol: "♥️", color: "red" },
    { id: 2, suit: "hearts", value: "2", symbol: "♥️", color: "red" },
    { id: 3, suit: "hearts", value: "3", symbol: "♥️", color: "red" },
    { id: 4, suit: "spades", value: "A", symbol: "♠️", color: "black" },
    { id: 5, suit: "spades", value: "2", symbol: "♠️", color: "black" },
  ];

  const [cards, setCards] = useState([]);
  const [selectedCards, setSelectedCards] = useState([]);
  const [attemptsLeft, setAttemptsLeft] = useState(3);
  const [gameStatus, setGameStatus] = useState("playing"); // 'playing', 'won', 'lost', 'final_chance'
  const [mode, setMode] = useState("random"); // 'random', 'evil'
  const [revealedCards, setRevealedCards] = useState(new Set());
  const [message, setMessage] = useState("选择两张牌，寻找两张红桃！");
  const [finalGuess1, setFinalGuess1] = useState("");
  const [finalGuess2, setFinalGuess2] = useState("");
  const [possibleSuitCombinations, setPossibleSuitCombinations] = useState([]);
  const [testRecords, setTestRecords] = useState([]);

  // 生成所有可能的花色组合（从5个位置中选3个放红桃）
  const generateAllSuitCombinations = () => {
    const combinations = [];
    for (let i = 0; i < 5; i++) {
      for (let j = i + 1; j < 5; j++) {
        for (let k = j + 1; k < 5; k++) {
          combinations.push([i, j, k]);
        }
      }
    }
    return combinations;
  };

  // 根据花色组合生成完整的牌组
  const generateCardsFromSuitCombination = (heartPositions) => {
    const newCards = new Array(5);
    const heartCards = initialCards.filter((card) => card.suit === "hearts");
    const spadeCards = initialCards.filter((card) => card.suit === "spades");

    // 先全部设为黑桃
    for (let i = 0; i < 5; i++) {
      newCards[i] = spadeCards[Math.floor(Math.random() * spadeCards.length)];
    }

    // 在指定位置放置红桃
    heartPositions.forEach((pos, index) => {
      newCards[pos] = heartCards[Math.floor(Math.random() * heartCards.length)];
    });

    return newCards;
  };
  
  const shuffleCards = (cardsArray) => {
    const shuffled = [...cardsArray];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // 恶意模式：从可能的花色组合中删除会成功的组合，并返回新的牌组
  const getEvilCards = (selectedPositions) => {
    if (mode !== "evil") return cards;

    const newCombinations = possibleSuitCombinations.filter((combination) => {
      // 检查这个花色组合是否会让玩家的选择成功（两个位置都是红桃）
      const wouldSucceed = selectedPositions.every((pos) =>
        combination.includes(pos)
      );
      return !wouldSucceed; // 保留不会成功的组合
    });

    setPossibleSuitCombinations(newCombinations);

    // 如果还有可能的组合，随机选择一个来生成当前的牌组
    if (newCombinations.length > 0) {
      const randomCombination =
        newCombinations[Math.floor(Math.random() * newCombinations.length)];
      const newCards = generateCardsFromSuitCombination(randomCombination);
      setCards(newCards);
      return newCards;
    }

    return cards;
  };

  // 添加测试记录
  const addTestRecord = (positions, result, type = "验证") => {
    const record = {
      id: Date.now(),
      attempt: type === "验证" ? (4 - attemptsLeft) : "最终",
      positions: [...positions].sort((a, b) => a - b),
      result: result,
      type: type
    };
    setTestRecords(prev => [...prev, record]);
  };

  // 初始化游戏
  const initGame = () => {
    if (mode === "evil") {
      // 恶意模式：初始化所有可能的花色组合
      const allSuitCombinations = generateAllSuitCombinations();
      setPossibleSuitCombinations(allSuitCombinations);
      // 随机选择一个初始花色组合
      const initialCombination =
        allSuitCombinations[
          Math.floor(Math.random() * allSuitCombinations.length)
        ];
      const initialCards = generateCardsFromSuitCombination(initialCombination);
      setCards(initialCards);
    } else {
      // 随机模式：正常洗牌
      const shuffled = shuffleCards(initialCards);
      setCards(shuffled);
      setPossibleSuitCombinations([]);
    }

    setSelectedCards([]);
    setAttemptsLeft(3);
    setGameStatus("playing");
    setRevealedCards(new Set());
    setMessage("选择两张牌，寻找两张红桃！");
    setFinalGuess1("");
    setFinalGuess2("");
    setTestRecords([]);
  };

  // 选择卡牌
  const selectCard = (index) => {
    if (gameStatus !== "playing" || revealedCards.has(index)) return;

    if (selectedCards.includes(index)) {
      setSelectedCards(selectedCards.filter((i) => i !== index));
    } else if (selectedCards.length < 2) {
      setSelectedCards([...selectedCards, index]);
    }
  };

  // 验证选择
  const verifySelection = () => {
    if (selectedCards.length !== 2) {
      setMessage("请选择两张牌！");
      return;
    }

    // 在恶意模式下获取更新后的牌组，在随机模式下使用当前牌组
    const currentCards = getEvilCards(selectedCards);

    const selectedCardObjs = selectedCards.map((index) => currentCards[index]);
    const isWin = selectedCardObjs.every((card) => card.suit === "hearts");

    // 记录本次测试
    addTestRecord(selectedCards, isWin ? "成功" : "失败", "验证");

    if (isWin) {
      setGameStatus("won");
      setRevealedCards(new Set(selectedCards));
      setMessage("🎉 恭喜！你找到了两张红桃！");
    } else {
      const newAttemptsLeft = attemptsLeft - 1;
      setAttemptsLeft(newAttemptsLeft);

      if (newAttemptsLeft === 0) {
        // 3次验证都失败后，锁定当前牌组作为最终牌组，不再变动
        setGameStatus("final_chance");
        setMessage(
          "⚠️ 3次验证都失败了！现在是最后一次机会：请在下方输入你认为是红桃的两张牌的位置（1-5）"
        );
      } else {
        setMessage(`❌ 这不是两张红桃！还剩 ${newAttemptsLeft} 次机会。`);
      }
    }

    setSelectedCards([]);
  };

  // 最终指认
  const finalVerification = () => {
    const pos1 = parseInt(finalGuess1) - 1;
    const pos2 = parseInt(finalGuess2) - 1;

    if (
      isNaN(pos1) ||
      isNaN(pos2) ||
      pos1 < 0 ||
      pos1 > 4 ||
      pos2 < 0 ||
      pos2 > 4
    ) {
      setMessage("请输入有效的位置数字（1-5）！");
      return;
    }

    if (pos1 === pos2) {
      setMessage("不能选择相同的位置！");
      return;
    }

    // 使用当前的牌组进行验证（已经是最终确定的）
    const finalSelectedCards = [pos1, pos2];
    const selectedCardObjs = finalSelectedCards.map((index) => cards[index]);
    const isWin = selectedCardObjs.every((card) => card.suit === "hearts");

    // 记录最终指认
    addTestRecord(finalSelectedCards, isWin ? "成功" : "失败", "最终指认");

    // 显示所有牌
    setRevealedCards(new Set([0, 1, 2, 3, 4]));

    if (isWin) {
      setGameStatus("won");
      setMessage("🎉🎉 太棒了！最后关头你成功找到了两张红桃！");
    } else {
      setGameStatus("lost");
      setMessage("😢 很遗憾，最终指认失败了。游戏结束！");
    }
  };

  // 重置选择
  const resetSelection = () => {
    setSelectedCards([]);
  };

  useEffect(() => {
    // 初始化游戏（只在组件挂载时运行一次）
    initGame();
  }, []);

  return (
    <div className="min-h-screen bg-green-800 p-4 md:p-8">
      <div className="bg-white rounded-lg shadow-2xl p-4 md:p-8 max-w-6xl mx-auto">
        <h1 className="text-2xl md:text-3xl font-bold text-center mb-6 text-gray-800">
          🃏 扑克牌抽取游戏
        </h1>

        {/* 模式选择 */}
        <div className="flex justify-center mb-6">
          <div className="bg-gray-100 rounded-lg p-1 flex">
            <button
              onClick={() => {
                if (mode !== "random") {
                  setMode("random");
                  const shuffled = shuffleCards(initialCards);
                  setCards(shuffled);
                  setPossibleSuitCombinations([]);
                  setSelectedCards([]);
                  setAttemptsLeft(3);
                  setGameStatus("playing");
                  setRevealedCards(new Set());
                  setMessage("选择两张牌，寻找两张红桃！");
                  setFinalGuess1("");
                  setFinalGuess2("");
                  setTestRecords([]);
                }
              }}
              className={`px-3 md:px-4 py-2 rounded-md transition-colors text-sm md:text-base ${
                mode === "random"
                  ? "bg-blue-500 text-white"
                  : "text-gray-600 hover:bg-gray-200"
              }`}
            >
              🎲 随机模式
            </button>
            <button
              onClick={() => {
                if (mode !== "evil") {
                  setMode("evil");
                  const allSuitCombinations = generateAllSuitCombinations();
                  setPossibleSuitCombinations(allSuitCombinations);
                  const initialCombination =
                    allSuitCombinations[
                      Math.floor(Math.random() * allSuitCombinations.length)
                    ];
                  const initialCards =
                    generateCardsFromSuitCombination(initialCombination);
                  setCards(initialCards);
                  setSelectedCards([]);
                  setAttemptsLeft(3);
                  setGameStatus("playing");
                  setRevealedCards(new Set());
                  setMessage("选择两张牌，寻找两张红桃！");
                  setFinalGuess1("");
                  setFinalGuess2("");
                  setTestRecords([]);
                }
              }}
              className={`px-3 md:px-4 py-2 rounded-md transition-colors text-sm md:text-base ${
                mode === "evil"
                  ? "bg-red-500 text-white"
                  : "text-gray-600 hover:bg-gray-200"
              }`}
            >
              😈 恶意模式
            </button>
          </div>
        </div>

        {/* 响应式布局：大屏左右分栏，小屏上下布局 */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* 游戏区域 */}
          <div className="flex-1">
            {/* 游戏信息 */}
            <div className="text-center mb-6">
              <p className="text-lg md:text-xl mb-2">{message}</p>
              <p className="text-base md:text-lg text-gray-600">
                剩余机会:{" "}
                <span className="font-bold text-red-600">{attemptsLeft}</span>
              </p>
            </div>

            {/* 卡牌区域 - 响应式调整 */}
            <div className="flex justify-center gap-2 md:gap-4 mb-6 overflow-x-auto">
              {cards.map((card, index) => (
                <div key={index} className="flex flex-col items-center flex-shrink-0">
                  {/* 位置编号 */}
                  <div className="text-xs md:text-sm text-gray-500 mb-1 font-semibold">
                    位置 {index + 1}
                  </div>
                  <div
                    onClick={() => selectCard(index)}
                    className={`
                      w-16 h-24 md:w-24 md:h-36 rounded-lg border-2 cursor-pointer transition-all duration-300 flex items-center justify-center
                      ${
                        selectedCards.includes(index)
                          ? "border-yellow-400 bg-yellow-100 transform scale-105"
                          : "border-gray-300 hover:border-blue-400 hover:bg-blue-50"
                      }
                      ${revealedCards.has(index) ? "bg-white" : "bg-blue-600"}
                      ${
                        gameStatus !== "playing" && !revealedCards.has(index)
                          ? "cursor-not-allowed opacity-50"
                          : ""
                      }
                    `}
                  >
                    {revealedCards.has(index) ? (
                      <div className="text-center">
                        <div
                          className={`text-sm md:text-2xl font-bold ${
                            card.color === "red" ? "text-red-600" : "text-black"
                          }`}
                        >
                          {card.value}
                        </div>
                        <div
                          className={`text-lg md:text-3xl ${
                            card.color === "red" ? "text-red-600" : "text-black"
                          }`}
                        >
                          {card.symbol}
                        </div>
                      </div>
                    ) : (
                      <div className="text-white text-sm md:text-lg font-bold">🂠</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* 最终指认界面 */}
            {gameStatus === "final_chance" && (
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 md:p-6 mb-6">
                <h3 className="text-lg md:text-xl font-bold text-red-700 mb-4 text-center">
                  🎯 最后一次机会！
                </h3>
                <p className="text-center mb-4 text-gray-700 text-sm md:text-base">
                  请输入你认为是红桃的两张牌的位置（输入1-5的数字）
                </p>
                <div className="flex justify-center gap-4 items-center mb-4">
                  <div className="flex flex-col items-center">
                    <label className="text-xs md:text-sm text-gray-600 mb-1">
                      第一张牌位置
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      value={finalGuess1}
                      onChange={(e) => setFinalGuess1(e.target.value)}
                      className="w-12 h-10 md:w-16 md:h-12 text-center text-lg md:text-xl font-bold border-2 border-gray-300 rounded-lg focus:border-red-500 focus:outline-none"
                      placeholder="?"
                    />
                  </div>
                  <div className="text-xl md:text-2xl text-gray-400">+</div>
                  <div className="flex flex-col items-center">
                    <label className="text-xs md:text-sm text-gray-600 mb-1">
                      第二张牌位置
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      value={finalGuess2}
                      onChange={(e) => setFinalGuess2(e.target.value)}
                      className="w-12 h-10 md:w-16 md:h-12 text-center text-lg md:text-xl font-bold border-2 border-gray-300 rounded-lg focus:border-red-500 focus:outline-none"
                      placeholder="?"
                    />
                  </div>
                </div>
                <div className="flex justify-center">
                  <button
                    onClick={finalVerification}
                    className="px-6 md:px-8 py-2 md:py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 font-bold text-base md:text-lg"
                  >
                    🎯 最终确认
                  </button>
                </div>
              </div>
            )}

            {/* 操作按钮 */}
            <div className="flex flex-wrap justify-center gap-2 md:gap-4 mb-6">
              {gameStatus === "playing" && (
                <>
                  <button
                    onClick={verifySelection}
                    disabled={selectedCards.length !== 2}
                    className="px-4 md:px-6 py-2 md:py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold text-sm md:text-base"
                  >
                    验证选择
                  </button>
                  <button
                    onClick={resetSelection}
                    className="px-4 md:px-6 py-2 md:py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-semibold text-sm md:text-base"
                  >
                    重新选择
                  </button>
                </>
              )}
              <button
                onClick={initGame}
                className="px-4 md:px-6 py-2 md:py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-semibold text-sm md:text-base"
              >
                🎮 新游戏
              </button>
            </div>
          </div>

          {/* 测试记录区域 */}
          <div className="w-full lg:w-80">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-bold text-base md:text-lg mb-4 text-gray-800 flex items-center">
                📋 测试记录
                {testRecords.length > 0 && (
                  <span className="ml-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                    {testRecords.length}
                  </span>
                )}
              </h3>
              
              {testRecords.length === 0 ? (
                <p className="text-gray-500 text-center py-4 text-sm">暂无测试记录</p>
              ) : (
                <div className="space-y-2 max-h-64 lg:max-h-96 overflow-y-auto">
                  {testRecords.map((record) => (
                    <div
                      key={record.id}
                      className={`p-3 rounded-lg border-l-4 ${
                        record.result === "成功"
                          ? "bg-green-50 border-green-400"
                          : "bg-red-50 border-red-400"
                      }`}
                    >
                      <div className="flex justify-between items-start mb-1">
                        <span className="font-semibold text-sm">
                          {record.type} #{record.attempt}
                        </span>
                        <span
                          className={`text-xs px-2 py-1 rounded-full ${
                            record.result === "成功"
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {record.result}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        选择位置: {record.positions.map(pos => pos + 1).join(", ")}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 游戏说明 */}
        <div className="bg-gray-50 rounded-lg p-4 text-xs md:text-sm text-gray-600 mt-6">
          <h3 className="font-bold mb-2">游戏规则：</h3>
          <ul className="space-y-1">
            <li>• 5张牌中有3张红桃，2张黑桃</li>
            <li>• 你有3次机会选择两张牌进行验证</li>
            <li>
              •{" "}
              <span className="text-orange-600 font-semibold">
                如果3次验证都失败，还有最后一次指认机会
              </span>
            </li>
            <li>• 最终指认需要输入两张牌的位置数字（1-5），这是最后的机会</li>
            <li>
              • <span className="text-blue-600 font-semibold">随机模式</span>
              ：完全随机洗牌
            </li>
            <li>
              • <span className="text-red-600 font-semibold">恶意模式</span>
              ：系统会分析你的选择，动态调整布局以避免你成功
            </li>
            <li>
              • <span className="text-purple-600 font-semibold">测试记录</span>
              ：记录每次选择和结果，帮助分析游戏策略
            </li>
          </ul>
        </div>
      </div>
      <footer className="mt-8 w-full text-center text-xs md:text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default PokerCardGame;
